/* De CRM-adapter voor het EMS-dashboard (F9.4, ADR 0009).
 *
 * In greenvision-ems leest de browser niets zelf: drie serverless routes
 * (api/assets.ts, api/kwartieren.ts, api/dagtotalen.ts) lezen onder de rol
 * ems_ingest en sturen JSON terug. Hier leest de CRM-sessie zelf, via PostgREST
 * op het schema `ems`, met de policies die er al staan (has_role admin,
 * super_admin of crm_user). Dit bestand levert **exact de vorm** van die drie
 * routes, zodat bouwHistorie() en de tekening het verschil niet kennen.
 *
 * Wat hier bewust NIET gebeurt:
 *   - ems.v_dagtotaal en ems.v_laatste_stand worden niet bevraagd. Die views
 *     zijn eigendom van postgres en staan op security_invoker = false: wie ze
 *     leest, leest alle locaties langs de policies heen. api/dagtotalen.ts
 *     herhaalt daarom de uitdrukking van de view; dit bestand ook.
 *   - ems.meting_ruw komt niet voor. authenticated heeft er geen recht op.
 *   - Er wordt niets geschreven.
 *
 * De metingen komen sinds migratie 108 (ADR 0032) niet meer als losse rijen
 * binnen maar als één samenvatting uit ems.dashboard_samenvatting(): compacte
 * reeksen per asset, veld en eenheid, de dagtotalen van de zon en de prijzen,
 * in één RPC-aanroep. Die functie leest als de aanroeper (security invoker),
 * dus dezelfde policies gelden. rijenUitSamenvatting() zet de reeksen terug in
 * de rijvorm van api/kwartieren.ts, zodat bouwHistorie() niets merkt.
 *
 * De twee valkuilen die hier worden afgevangen:
 *   1. max_rows = 1000 op de Data API. PostgREST kapt zonder foutmelding af.
 *      Voor de metingen is dat voorbij (een RPC heeft geen plafond); voor
 *      locatie, assets en runs geldt het nog. Zie haalAllePaginas(): eerst een
 *      exacte telling, dan pagina voor pagina tot die telling gehaald is, en
 *      een harde fout als dat niet lukt.
 *   2. RLS weigert een SELECT niet, hij filtert. Een gebruiker zonder recht
 *      krijgt nul rijen en geen fout. Nul rijen op ems.locatie wordt daarom
 *      niet als "bestaat niet" gelezen voordat has_role() — dezelfde functie
 *      als in de policy — heeft bevestigd dat de gebruiker wél mag lezen. De
 *      samenvatting doet hetzelfde: null terug als de locatie onzichtbaar is.
 */

import { eisZone, beginVanDag, dagSleutelPlus, dagReeks, vandaagSleutel, isDagsleutel, dagenTussen } from '../../dashboard/data/tijd';
import { bouwHistorie, controleerDagtotalen, zetPrijzen } from '../../dashboard/data/vorm';
import { bouwServicestatus } from '../../dashboard/data/service';
import { leidBronnenAf } from '../../dashboard/data/bronnen';
import { VELD_PV as VELD_PV_GEDEELD } from '../../dashboard/data/rijen';
import { CRM_ROLLEN } from './toegang';
import type { DagtotaalRij } from '../../dashboard/data/maandrapport';

/* Het veld waarin de Solis-ingest het omvormervermogen wegschrijft. */
export const VELD_PV = VELD_PV_GEDEELD;

/**
 * Alle velden die deze datalaag ophaalt.
 *
 * Deze lijst en GELEZEN_VELDEN in dashboard/data/bronnen.ts horen gelijk te
 * lopen: die tweede beslist of een bron in de tekening wordt aangezet, deze
 * eerste bepaalt of er dan ook iets ís. Staat een veld in GELEZEN_VELDEN maar
 * niet hier, dan tekent het dashboard nullen alsof het metingen zijn — precies
 * de stille fout waar bronnen.ts voor waarschuwt. tests/ems/velden.node.test.mjs
 * bewaakt dat ze niet uit elkaar lopen.
 */
export const VELDEN_ZYMPLER = [
  'bat_soc', 'bat_energie', 'bat_laden', 'bat_ontladen', 'net_import', 'net_export',
  'bat_laden_kwh', 'bat_ontladen_kwh', 'net_import_kwh', 'net_export_kwh',
  /* de balans van de locatie; zie GELEZEN_VELDEN */
  'zon_locatie', 'zon_locatie_kwh', 'verbruik', 'verbruik_kwh', 'bat_soc_groen', 'bat_soc_grijs',
  'zon_naar_pand', 'zon_naar_onbekend', 'zon_naar_lader', 'zon_naar_bat', 'zon_naar_net',
  'bat_naar_pand', 'bat_naar_onbekend', 'bat_naar_lader', 'bat_naar_net',
  'net_naar_pand', 'net_naar_lader', 'net_naar_bat',
] as const;

/* Het laadvermogen per laadpunt, uit de laadsessies van de CPMS (Plugfuse,
   ADR 0039). Alleen het vermogen: het dagtotaal is vermogen/4, en de ingest
   schrijft kW = kWh × 4, dus laad_vermogen_kwh ophalen zou de rijen verdubbelen
   zonder iets toe te voegen. */
export const VELDEN_PLUGFUSE = ['laad_vermogen'] as const;

export const VELDEN = [VELD_PV, ...VELDEN_ZYMPLER, ...VELDEN_PLUGFUSE] as const;

/* Zelfde grens als lib/poort.ts in greenvision-ems. */
export const MAX_DAGEN = 62;

/* Zelfde aantal als RUNS in api/assets.ts. */
export const RUNS = 60;

/* Paginagrootte. Gelijk aan max_rows van de Data API. Staat max_rows lager, dan
   levert elke pagina minder dan gevraagd en schuift de lus op met wat er wél
   kwam — hij stopt niet omdat een pagina "te kort" is. */
export const PAGINA = 1000;

/* De rollen uit de policies intern_leest_* op ems.* staan in toegang.ts. */
export { CRM_ROLLEN };

/* De enige tabellen die deze adapter rechtstreeks aanraakt. De metingen en de
   prijzen komen via EMS_FUNCTIE (migratie 108); de dagtotalen (migratie 109)
   zijn weer een tabel, net als de laadsessies (migratie 111). */
export const EMS_TABELLEN = ['locatie', 'asset', 'ingest_run', 'dagtotaal', 'laadsessie'] as const;

/* De bronnen die laadsessies in ems.laadsessie schrijven (ADR 0039). Alleen voor
   laadpunten op zo'n bron worden sessies opgevraagd. */
export const LAADSESSIE_BRONNEN: readonly string[] = ['plugfuse'];

/* De samenvattingsfunctie in het schema ems (migratie 108, ADR 0032). */
export const EMS_FUNCTIE = 'dashboard_samenvatting';

const LOCATIE_KOLOMMEN = 'id, naam, adres, plaats, tijdzone, net_contract_kw, net_sturingsdrempel_kw, meta, actief';
/* bron_platform en parent_id staan niet in het antwoord van api/assets.ts (dat
   blijft exact gelijk), maar wel in `register`: daaruit volgen de bronnen van
   de locatie en welke gun aan welke lader hangt (F9.3). */
const ASSET_KOLOMMEN = 'id, type, naam, volgorde, merk, model, serienummer, datalogger_sn, extern_id, nominaal_kw, capaciteit_kwh, kwp, actief, meta, bron_platform, parent_id';
const RUN_KOLOMMEN = 'id, taak, status, records, gestart_op, klaar_op, fout';
/* ems.laadsessie (migratie 111). Geen idtag_pseudoniem: het scherm heeft hem niet nodig. */
const LAADSESSIE_KOLOMMEN = 'id, asset_id, extern_id, gestart_op, gestopt_op, meter_start_wh, meter_stop_wh, energie_wh, status, stopreden, controle';
/* ems.dagtotaal (migratie 109): één rij per lokale dag, zie dashboard/data/maandrapport.ts. */
const DAGTOTAAL_KOLOMMEN = 'dag, zon_kwh, zon_per_omvormer, net_import_kwh, net_export_kwh, bat_laden_kwh, bat_ontladen_kwh, verbruik_kwh, '
  + 'zon_naar_pand_kwh, zon_naar_bat_kwh, zon_naar_net_kwh, zon_naar_lader_kwh, bat_naar_pand_kwh, bat_naar_net_kwh, bat_naar_lader_kwh, '
  + 'net_naar_pand_kwh, net_naar_bat_kwh, net_naar_lader_kwh, piek_kw, piek_tijdstip, soc_min, soc_max, soc_einde, '
  + 'inkoop_eur, teruglevering_eur, kwartieren_gemeten, volledig';
/* Dagrijen zijn klein (één per dag): vijf jaar is 1.830 rijen, twee pagina's. */
export const MAX_DAGEN_DAGTOTAAL = 5 * 366;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/* ── fouten ─────────────────────────────────────────────────────── */

export type FoutSoort = 'geen-toegang' | 'niet-gevonden' | 'configuratie' | 'paginering' | 'invoer' | 'fout';

/**
 * Elke weigering en elke fout krijgt een soort, zodat het scherm "geen
 * toegang", "bestaat niet", "geen data" en "kapot" uit elkaar kan houden.
 */
export class EmsFout extends Error {
  soort: FoutSoort;
  code: string | null;
  constructor(soort: FoutSoort, bericht: string, code: string | null = null) {
    super(bericht);
    this.name = 'EmsFout';
    this.soort = soort;
    this.code = code;
  }
}

/** Zet een PostgREST-fout om in een EmsFout met de juiste soort. */
export function classificeer(error: any, status: number | undefined, waar: string): EmsFout {
  const code: string = error?.code ? String(error.code) : '';
  const bericht: string = error?.message ? String(error.message) : String(error);
  if (code === 'PGRST106') {
    return new EmsFout('configuratie',
      `${waar}: het schema ems is niet bereikbaar via de Data API. Staat ems onder Settings → API → Exposed schemas? (${bericht})`, code);
  }
  if (code === '42501') {
    return new EmsFout('geen-toegang', `${waar}: geen recht op deze tabel (${bericht})`, code);
  }
  if (code === 'PGRST301' || code === 'PGRST302' || code === 'PGRST303' || status === 401) {
    return new EmsFout('geen-toegang', `${waar}: de sessie is niet (meer) geldig (${bericht})`, code || String(status));
  }
  if (code === '22P02') {
    return new EmsFout('niet-gevonden', `${waar}: ongeldige sleutel (${bericht})`, code);
  }
  /* 42P01 is Postgres (relation does not exist), PGRST205 is PostgREST (de tabel
     staat niet in zijn schemacache). Allebei: de migratie is nog niet toegepast. */
  if (code === '42P01' || code === 'PGRST205') {
    return new EmsFout('configuratie', `${waar}: de tabel bestaat niet; is de migratie toegepast? (${bericht})`, code);
  }
  return new EmsFout('fout', `${waar}: ${bericht}`, code || null);
}

/* ── client ─────────────────────────────────────────────────────── */

/** Het deel van de supabase-js-client dat hier gebruikt wordt. */
export interface EmsClient {
  /* .schema('ems').from(tabel) voor de tabellen, .schema('ems').rpc(fn) voor de samenvatting */
  schema(naam: string): {
    from(tabel: string): any;
    rpc(fn: string, args: Record<string, unknown>): PromiseLike<{ data: any; error: any; status?: number }>;
  };
  /* public.has_role, voor de duiding van nul rijen */
  rpc(fn: string, args: Record<string, unknown>): PromiseLike<{ data: any; error: any }>;
  auth: { getSession(): PromiseLike<{ data: { session: { user: { id: string } } | null }; error: any }> };
}

function ems(client: EmsClient, tabel: (typeof EMS_TABELLEN)[number]) {
  return client.schema('ems').from(tabel);
}

/* ── paginering ─────────────────────────────────────────────────── */

type Pagina<T> = { data: T[] | null; error: any; count?: number | null; status?: number };

/**
 * Haalt een lijst op tot en met de laatste rij, ook boven max_rows.
 *
 * - De eerste pagina vraagt een exacte telling. Zonder telling is niet te
 *   bewijzen dat alles binnen is, en dan valt dit om in plaats van te gokken.
 * - Er wordt opgeschoven met het aantal rijen dat wérkelijk terugkwam, niet met
 *   de paginagrootte. Een server die per pagina minder levert dan gevraagd
 *   slaat dus niets over.
 * - Het einde is een lege pagina, niet het bereiken van de telling. Komt er
 *   tijdens het ophalen een rij bij ná de huidige positie, dan schuift de
 *   laatste rij voorbij de telling; wie bij de telling stopt mist hem stil.
 * - Een lege pagina terwijl de telling nog niet gehaald is, is een fout (er is
 *   tijdens het ophalen iets verdwenen, dus er is een rij overgeslagen).
 * - Met `sleutel` worden dubbele rijen gezien. Die ontstaan als er tijdens het
 *   ophalen een rij vóór de huidige positie bijkomt (een backfill). Dan wordt
 *   één keer opnieuw begonnen; lukt het dan nog niet, dan is het een fout —
 *   een dubbel kwartier zou anders stil twee keer in het dagtotaal tellen.
 * - Met `limiet` (bijvoorbeeld de 60 runs) stopt hij bij de limiet.
 *
 * @param bouw  maakt de query voor rijen [van, tot] (inclusief), met telling
 *              alleen op de eerste pagina
 */
export async function haalAllePaginas<T>(
  waar: string,
  bouw: (van: number, tot: number, telling: boolean) => PromiseLike<Pagina<T>>,
  opties: { pagina?: number; limiet?: number; sleutel?: (rij: T) => string } = {},
): Promise<{ rijen: T[]; totaal: number }> {
  const pagina = opties.pagina ?? PAGINA;
  const limiet = opties.limiet ?? Infinity;

  for (let poging = 1; poging <= 2; poging++) {
    const rijen: T[] = [];
    const gezien = opties.sleutel ? new Set<string>() : null;
    let totaal: number | null = null;
    let dubbel = false;

    for (;;) {
      const van = rijen.length;
      const gewenst = Math.min(pagina, limiet - van);
      if (gewenst <= 0) break; // limiet bereikt

      const antwoord = await bouw(van, van + gewenst - 1, totaal === null);
      if (antwoord.error) throw classificeer(antwoord.error, antwoord.status, waar);
      const data = antwoord.data ?? [];

      if (totaal === null) {
        if (typeof antwoord.count !== 'number') {
          throw new EmsFout('paginering', `${waar}: de Data API gaf geen telling terug, dus is niet vast te stellen of alle rijen binnen zijn`);
        }
        totaal = antwoord.count;
      }

      if (data.length === 0) {
        if (rijen.length < Math.min(totaal, limiet)) {
          throw new EmsFout('paginering',
            `${waar}: pagina vanaf rij ${van} was leeg terwijl er ${Math.min(totaal, limiet)} rijen geteld zijn — ${rijen.length} binnen`);
        }
        break; // het echte einde
      }

      for (const rij of data) {
        if (gezien) {
          const k = opties.sleutel!(rij);
          if (gezien.has(k)) { dubbel = true; continue; }
          gezien.add(k);
        }
        rijen.push(rij);
      }
      if (dubbel) break;
    }

    if (!dubbel) return { rijen, totaal: totaal ?? rijen.length };
  }
  throw new EmsFout('paginering', `${waar}: de reeks veranderde twee keer tijdens het ophalen (dubbele rijen); probeer opnieuw`);
}

/* ── toegang ────────────────────────────────────────────────────── */

/**
 * Mag de huidige sessie ems lezen? Stelt dezelfde vraag als de policy:
 * has_role(auth.uid(), admin|super_admin|crm_user).
 *
 * Alleen nodig om nul rijen te duiden. Een fout hier wordt geen "nee", maar
 * een fout: anders zou een storing als weigering op het scherm komen.
 */
export async function magEmsLezen(client: EmsClient): Promise<boolean> {
  const { data, error } = await client.auth.getSession();
  if (error) throw classificeer(error, undefined, 'sessie');
  const userId = data?.session?.user?.id;
  if (!userId) return false;
  const antwoorden = await Promise.all(
    CRM_ROLLEN.map((rol) => client.rpc('has_role', { _user_id: userId, _role: rol })),
  );
  const mis = antwoorden.find((a) => a.error);
  if (mis) throw classificeer(mis.error, undefined, 'has_role');
  return antwoorden.some((a) => a.data === true);
}

/** Nul rijen waar er een verwacht werd: weigering of werkelijk afwezig? */
async function duidLeeg(client: EmsClient, watNietGevonden: string): Promise<EmsFout> {
  if (!(await magEmsLezen(client))) {
    return new EmsFout('geen-toegang',
      'Deze sessie mag het schema ems niet lezen. Alleen admin, super_admin en crm_user hebben een policy op ems.');
  }
  return new EmsFout('niet-gevonden', watNietGevonden);
}

/* ── omzetten naar de vorm van de routes ────────────────────────── */

/* `::float8` in de SQL. PostgREST levert numeric als getal, maar niet elke
   laag doet dat; null blijft null. */
function float(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function tekst(v: unknown): string | null {
  return v === null || v === undefined ? null : String(v);
}

/* `to_char(t at time zone 'UTC', 'YYYY-MM-DD"T"HH24:MI:SS"Z"')`: vaste lengte,
   seconden afgekapt. Het front-end vergelijkt tijdstippen als tekst. */
export function utcSeconden(v: unknown): string {
  const d = v instanceof Date ? v : typeof v === 'number' ? new Date(v) : new Date(String(v));
  if (Number.isNaN(d.getTime())) throw new EmsFout('fout', `ongeldig tijdstip uit ems.meting: ${String(v)}`);
  return `${d.toISOString().slice(0, 19)}Z`;
}

/* Wat node-postgres van een timestamptz maakt, zoals /api/assets hem uitstuurde. */
function isoMs(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? String(v) : d.toISOString();
}

/* `left(fout, 200)`: tweehonderd tekens, geen UTF-16-eenheden. */
function links(v: unknown, n: number): string | null {
  if (v === null || v === undefined) return null;
  return Array.from(String(v)).slice(0, n).join('');
}

/* ── de drie routes ─────────────────────────────────────────────── */

export interface AssetsAntwoord {
  locatie: {
    naam: string; adres: string | null; plaats: string | null; tijdzone: string;
    net_contract_kw: number | null; net_sturingsdrempel_kw: number | null; meta: any;
  };
  assets: any[];
  runs: any[];
}

export interface LocatieContext {
  locatieId: string;
  antwoord: AssetsAntwoord;
  /* Alle assets van de locatie, óók de inactieve. api/kwartieren.ts en
     api/dagtotalen.ts joinen ems.asset zonder filter op a.actief; alleen
     api/assets.ts filtert. Dat verschil wordt hier gespiegeld. */
  alleAssetIds: string[];
  assetNamen: Map<string, string>;
  /* De actieve assets in de vorm van `antwoord.assets`, plus bron_platform en
     parent_id. Voedt bouwConfiguratie(); de routevorm blijft ongemoeid. */
  register: any[];
}

/**
 * Tegenhanger van GET /api/assets. Levert `antwoord` in precies de vorm van die
 * route, plus wat de andere twee routes nodig hebben.
 */
export async function haalAssets(client: EmsClient, locatieId: string): Promise<LocatieContext> {
  if (typeof locatieId !== 'string' || !UUID.test(locatieId)) {
    throw new EmsFout('niet-gevonden', `"${String(locatieId)}" is geen geldige locatie-id`);
  }

  const [locaties, assets, runs] = await Promise.all([
    haalAllePaginas<any>('ems.locatie', (van, tot, telling) =>
      ems(client, 'locatie').select(LOCATIE_KOLOMMEN, telling ? { count: 'exact' } : undefined)
        .eq('id', locatieId).eq('actief', true)
        .range(van, tot), { limiet: 1 }),
    haalAllePaginas<any>('ems.asset', (van, tot, telling) =>
      ems(client, 'asset').select(ASSET_KOLOMMEN, telling ? { count: 'exact' } : undefined)
        .eq('locatie_id', locatieId)
        .order('volgorde', { ascending: true }).order('naam', { ascending: true }).order('id', { ascending: true })
        .range(van, tot), { sleutel: (r) => String(r.id) }),
    haalAllePaginas<any>('ems.ingest_run', (van, tot, telling) =>
      ems(client, 'ingest_run').select(RUN_KOLOMMEN, telling ? { count: 'exact' } : undefined)
        .eq('locatie_id', locatieId)
        .order('gestart_op', { ascending: false }).order('id', { ascending: false })
        .range(van, tot), { limiet: RUNS, sleutel: (r) => String(r.id) }),
  ]);

  if (!locaties.rijen.length) {
    throw await duidLeeg(client, `Er is geen actieve locatie met id ${locatieId} in ems.locatie.`);
  }
  const l = locaties.rijen[0];
  try {
    eisZone(l.tijdzone);
  } catch {
    throw new EmsFout('configuratie', `ems.locatie ${locatieId} heeft geen tijdzone; zonder zone vallen kwartieren op de verkeerde dag`);
  }

  const antwoord: AssetsAntwoord = {
    locatie: {
      naam: l.naam, adres: l.adres, plaats: l.plaats, tijdzone: l.tijdzone,
      net_contract_kw: float(l.net_contract_kw),
      net_sturingsdrempel_kw: float(l.net_sturingsdrempel_kw),
      meta: l.meta,
    },
    assets: assets.rijen.filter((a) => a.actief === true).map((a) => ({
      id: tekst(a.id), type: tekst(a.type), naam: a.naam, volgorde: a.volgorde,
      merk: a.merk, model: a.model, serienummer: a.serienummer, datalogger_sn: a.datalogger_sn,
      extern_id: a.extern_id,
      nominaal_kw: float(a.nominaal_kw), capaciteit_kwh: float(a.capaciteit_kwh), kwp: float(a.kwp),
      actief: a.actief, meta: a.meta,
    })),
    runs: runs.rijen.map((r) => ({
      id: tekst(r.id), taak: r.taak, status: r.status, records: r.records,
      gestart_op: isoMs(r.gestart_op), klaar_op: isoMs(r.klaar_op),
      fout: links(r.fout, 200),
    })),
  };

  return {
    locatieId,
    antwoord,
    alleAssetIds: assets.rijen.map((a) => String(a.id)),
    assetNamen: new Map(assets.rijen.map((a) => [String(a.id), a.naam])),
    register: assets.rijen.filter((a) => a.actief === true).map((a) => ({
      id: tekst(a.id), type: tekst(a.type), naam: a.naam, volgorde: a.volgorde,
      merk: a.merk, model: a.model, serienummer: a.serienummer, datalogger_sn: a.datalogger_sn,
      extern_id: a.extern_id,
      nominaal_kw: float(a.nominaal_kw), capaciteit_kwh: float(a.capaciteit_kwh), kwp: float(a.kwp),
      actief: a.actief, meta: a.meta,
      bron_platform: tekst(a.bron_platform), parent_id: tekst(a.parent_id),
    })),
  };
}

/** Controleert van/tot zoals leesBereik() in lib/poort.ts. */
export function eisBereik(van: string, tot: string): void {
  if (!isDagsleutel(van) || !isDagsleutel(tot) || van > tot) {
    throw new EmsFout('invoer', 'van en tot moeten JJJJ-MM-DD zijn, van niet na tot');
  }
  if (dagenTussen(van, tot) > MAX_DAGEN) {
    throw new EmsFout('invoer', `hoogstens ${MAX_DAGEN} dagen per verzoek`);
  }
}

/* ── de samenvatting (migratie 108) ─────────────────────────────── */

/** Wat ems.dashboard_samenvatting() teruggeeft. */
export interface Samenvatting {
  locatie_id: string;
  tijdzone: string;
  /** het venster in UTC, zoals gevraagd */
  vanaf: string;
  tot: string;
  sinds: string | null;
  /** per asset, veld en eenheid: t = seconden na `vanaf`, w = de waarde, in tijdvolgorde */
  reeksen: { asset_id: string; veld: string; eenheid: string | null; t: number[]; w: (number | string | null)[] }[];
  /** sum(waarde)/4 van pv_vermogen in kW per omvormer en lokale dag; null bij een incrementele lees */
  dagtotalen: { asset_id: string; asset: string | null; dag: string; kwh: number | string | null }[] | null;
  /** ems.prijs in het venster: t = seconden na `vanaf`, d = duur_min, p = prijs_eur_kwh; null zonder prijzen */
  prijzen: { t: number[]; d: (number | string)[]; p: (number | string | null)[] } | null;
}

/**
 * Eén aanroep voor de metingen, de dagtotalen en de prijzen van alle assets van
 * de locatie, van lokale middernacht op `van` tot lokale middernacht na `tot`,
 * in de zone van de locatie. De functie leest als de aanroeper, dus nul
 * zichtbare locaties geeft null — en dat wordt hier net als elders geduid met
 * has_role(): geen toegang, of bestaat niet.
 */
export async function haalSamenvatting(client: EmsClient, locatieId: string, van: string, tot: string, zone: string): Promise<Samenvatting> {
  eisBereik(van, tot);
  eisZone(zone);
  /* ($van::date)::timestamp at time zone l.tijdzone  en
     ($tot::date + 1)::timestamp at time zone l.tijdzone */
  const vanaf = beginVanDag(van, zone).toISOString();
  const totAan = beginVanDag(dagSleutelPlus(tot, 1), zone).toISOString();

  const antwoord = await client.schema('ems').rpc(EMS_FUNCTIE, {
    p_locatie: locatieId,
    p_vanaf: vanaf,
    p_tot: totAan,
    p_velden: VELDEN as unknown as string[],
    p_prijzen: true,
  });
  if (antwoord.error) throw classificeer(antwoord.error, antwoord.status, `ems.${EMS_FUNCTIE}`);
  if (antwoord.data === null || antwoord.data === undefined) {
    throw await duidLeeg(client, `Er is geen locatie met id ${locatieId} in ems.locatie.`);
  }
  const s = antwoord.data as Samenvatting;
  if (!Array.isArray(s.reeksen)) {
    throw new EmsFout('fout', `ems.${EMS_FUNCTIE}: onverwacht antwoord zonder reeksen`);
  }
  return s;
}

/**
 * Van de compacte reeksen terug naar de rijen van api/kwartieren.ts, in de
 * volgorde die de Data API gaf (tijdstip, asset_id, veld). Daardoor tekent
 * bouwHistorie() precies wat hij vóór migratie 108 tekende: dezelfde rijen in
 * dezelfde volgorde, dus ook dezelfde optelling.
 */
export function rijenUitSamenvatting(s: Samenvatting): {
  kwartieren: { asset_id: string; tijdstip: string; veld: string; eenheid: string | null; waarde: number | null }[];
  dagtotalen: { asset_id: string; asset: string | null; dag: string; kwh: number | null }[];
  prijzen: { tijdstip: string; duur_min: number; prijs_eur_kwh: number }[];
} {
  const begin = Date.parse(s.vanaf);
  if (!Number.isFinite(begin)) throw new EmsFout('fout', `ems.${EMS_FUNCTIE}: ongeldig venster ${String(s.vanaf)}`);

  const kwartieren: { asset_id: string; tijdstip: string; veld: string; eenheid: string | null; waarde: number | null }[] = [];
  for (const r of s.reeksen) {
    const t = Array.isArray(r.t) ? r.t : [];
    const w = Array.isArray(r.w) ? r.w : [];
    if (t.length !== w.length) throw new EmsFout('fout', `ems.${EMS_FUNCTIE}: reeks ${r.asset_id}/${r.veld} heeft ${t.length} tijden en ${w.length} waarden`);
    const asset_id = String(r.asset_id);
    const veld = String(r.veld);
    const eenheid = r.eenheid ?? null;
    for (let i = 0; i < t.length; i++) {
      kwartieren.push({ asset_id, tijdstip: utcSeconden(begin + Number(t[i]) * 1000), veld, eenheid, waarde: float(w[i]) });
    }
  }
  kwartieren.sort((a, b) => (a.tijdstip < b.tijdstip ? -1 : a.tijdstip > b.tijdstip ? 1
    : a.asset_id < b.asset_id ? -1 : a.asset_id > b.asset_id ? 1
      : a.veld < b.veld ? -1 : a.veld > b.veld ? 1 : 0));

  const dagtotalen = (s.dagtotalen ?? []).map((d) => ({
    asset_id: String(d.asset_id), asset: d.asset === null || d.asset === undefined ? null : String(d.asset),
    dag: String(d.dag).slice(0, 10), kwh: float(d.kwh),
  }));

  const prijzen: { tijdstip: string; duur_min: number; prijs_eur_kwh: number }[] = [];
  const p = s.prijzen;
  if (p && Array.isArray(p.t) && Array.isArray(p.d) && Array.isArray(p.p)) {
    for (let i = 0; i < p.t.length; i++) {
      const prijs = float(p.p[i]);
      /* Een ontbrekende prijs is geen prijs, niet € 0. */
      if (prijs === null) continue;
      prijzen.push({ tijdstip: new Date(begin + Number(p.t[i]) * 1000).toISOString(), duur_min: Number(p.d[i]), prijs_eur_kwh: prijs });
    }
  }
  return { kwartieren, dagtotalen, prijzen };
}

/* ── de rekenregels, als referentie ─────────────────────────────── */
/*
 * alsKwartieren() en alsDagtotalen() staan sinds het klantportaal in
 * dashboard/data/rijen.ts: het portaal en ems-app tekenen op losse rijen uit de
 * klantfuncties en mogen niets uit crm/ importeren. Hier alleen doorgegeven,
 * zodat de toetsen ze op dezelfde plek blijven vinden.
 */
export { alsKwartieren, alsDagtotalen } from '../../dashboard/data/rijen';

/* ── laadsessies ────────────────────────────────────────────────── */

/**
 * De afgeronde laadsessies van deze laadpunten die op of na `vanaf` stopten,
 * nieuwste eerst. Leeg zonder laadpunten.
 */
export async function haalLaadsessies(client: EmsClient, laadpunten: readonly string[], vanaf: string): Promise<any[]> {
  if (!laadpunten.length) return [];
  const { rijen } = await haalAllePaginas<any>('ems.laadsessie', (van, tot, telling) =>
    ems(client, 'laadsessie').select(LAADSESSIE_KOLOMMEN, telling ? { count: 'exact' } : undefined)
      .in('asset_id', laadpunten as string[])
      .gte('gestopt_op', vanaf)
      .order('gestopt_op', { ascending: false }).order('id', { ascending: false })
      .range(van, tot), { sleutel: (r) => String(r.id) });
  return rijen.map((r) => ({
    id: tekst(r.id), asset_id: tekst(r.asset_id), extern_id: r.extern_id,
    gestart_op: isoMs(r.gestart_op), gestopt_op: isoMs(r.gestopt_op),
    meter_start_wh: float(r.meter_start_wh), meter_stop_wh: float(r.meter_stop_wh), energie_wh: float(r.energie_wh),
    status: r.status, stopreden: r.stopreden ?? null, controle: r.controle ?? null,
  }));
}

/* ── alles in één keer ──────────────────────────────────────────── */

/**
 * Tegenhanger van haalAlles() in web/src/data/ems.js. Twee golven in plaats van
 * één: de tijdzone komt uit de locatie, en die is nodig om het venster te
 * bepalen. In de bron zat de zone vast in de bundel en stond de locatie in het
 * koekje. De tweede golf is sinds migratie 108 één aanroep.
 */
export async function haalAlles(client: EmsClient, locatieId: string, opties: { dagenTerug?: number; nu?: Date } = {}) {
  const nu = opties.nu ?? new Date();
  const ctx = await haalAssets(client, locatieId);
  const { locatie, assets, runs } = ctx.antwoord;
  const zone = locatie.tijdzone;

  const venster = Math.min(Math.max(1, opties.dagenTerug ?? 31), MAX_DAGEN);
  const tot = vandaagSleutel(zone, nu);
  const van = dagSleutelPlus(tot, -(venster - 1));
  const dagen = dagReeks(van, tot);

  const samenvatting = await haalSamenvatting(client, locatieId, van, tot, zone);
  const { kwartieren, dagtotalen, prijzen } = rijenUitSamenvatting(samenvatting);

  const historie = bouwHistorie(assets, kwartieren, dagtotalen, dagen, zone);
  zetPrijzen(historie, prijzen, zone);

  /* De laadsessies voor het ERE-tabblad. Aanvullend: lukt het niet (bijvoorbeeld
     omdat migratie 111 nog niet is toegepast), dan tekent het dashboard gewoon,
     zonder sessielijst. null is "niet opgehaald", [] is "geen sessies". */
  const laadpunten = ctx.register
    .filter((a) => a.type === 'gun' && LAADSESSIE_BRONNEN.includes(a.bron_platform))
    .map((a) => String(a.id));
  let laadsessies: any[] | null = null;
  if (laadpunten.length) {
    try {
      laadsessies = await haalLaadsessies(client, laadpunten, beginVanDag(van, zone).toISOString());
    } catch (e) {
      console.warn('[ems] laadsessies niet opgehaald', e);
    }
  }
  const service = bouwServicestatus(assets, historie, runs, nu, zone);
  const afwijkingen = controleerDagtotalen(historie);
  if (afwijkingen.length) {
    console.warn('[ems] dagtotaal wijkt af van de som van de kwartieren', afwijkingen);
  }

  return {
    locatieId,
    locatie, assets, historie, afwijkingen, runs, service, laadsessies,
    register: ctx.register,
    /* Per locatie, uit de eigen assets. Geen constante meer (F9.3). */
    bronnen: leidBronnenAf(ctx.register),
    /* Zodat "geen data" op het scherm met getallen onderbouwd kan worden in
       plaats van een lege grafiek. */
    telling: {
      van, tot, dagen: dagen.length,
      assetsZichtbaar: ctx.alleAssetIds.length,
      metingen: kwartieren.length,
      grenzen: { vanaf: samenvatting.vanaf, tot: samenvatting.tot },
    },
  };
}

/* ── de dagtotalen (migratie 109, ADR 0033) ─────────────────────── */


/* alsDagtotaal() staat sinds het klantportaal in dashboard/data/rijen.ts: het
   portaal krijgt dezelfde dagrijen uit client-ems-samenvatting. */
export { alsDagtotaal } from '../../dashboard/data/rijen';
import { alsDagtotaal } from '../../dashboard/data/rijen';

/** Controleert van/tot voor dagrijen: zelfde vorm als eisBereik, ruimere grens. */
export function eisBereikDagtotalen(van: string, tot: string): void {
  if (!isDagsleutel(van) || !isDagsleutel(tot) || van > tot) {
    throw new EmsFout('invoer', 'van en tot moeten JJJJ-MM-DD zijn, van niet na tot');
  }
  if (dagenTussen(van, tot) > MAX_DAGEN_DAGTOTAAL) {
    throw new EmsFout('invoer', `hoogstens ${MAX_DAGEN_DAGTOTAAL} dagen per verzoek`);
  }
}

/**
 * De dagtotalen van één locatie van `van` tot en met `tot` (dagsleutels in de
 * zone van de locatie; de kolom `dag` is al lokaal). Dezelfde paginering en
 * foutclassificatie als de metingen. Een ontbrekende tabel (migratie 109 nog
 * niet toegepast) is een 'configuratie'-fout, geen lege lijst.
 */
export async function haalDagtotalen(client: EmsClient, locatieId: string, van: string, tot: string): Promise<DagtotaalRij[]> {
  if (typeof locatieId !== 'string' || !UUID.test(locatieId)) {
    throw new EmsFout('niet-gevonden', `"${String(locatieId)}" is geen geldige locatie-id`);
  }
  eisBereikDagtotalen(van, tot);
  const { rijen } = await haalAllePaginas<any>('ems.dagtotaal', (a, b, telling) =>
    ems(client, 'dagtotaal').select(DAGTOTAAL_KOLOMMEN, telling ? { count: 'exact' } : undefined)
      .eq('locatie_id', locatieId)
      .gte('dag', van)
      .lte('dag', tot)
      .order('dag', { ascending: true })
      .range(a, b),
  { sleutel: (r) => String(r.dag) });
  return rijen.map(alsDagtotaal);
}

/** De eerste en de laatste dag met een dagtotaal; null als er geen rij is. */
export async function haalDagtotaalBereik(client: EmsClient, locatieId: string): Promise<{ eersteDag: string; laatsteDag: string } | null> {
  if (typeof locatieId !== 'string' || !UUID.test(locatieId)) {
    throw new EmsFout('niet-gevonden', `"${String(locatieId)}" is geen geldige locatie-id`);
  }
  const grens = (oplopend: boolean) => haalAllePaginas<any>('ems.dagtotaal', (a, b, telling) =>
    ems(client, 'dagtotaal').select('dag', telling ? { count: 'exact' } : undefined)
      .eq('locatie_id', locatieId)
      .order('dag', { ascending: oplopend })
      .range(a, b), { limiet: 1 });
  const [eerste, laatste] = await Promise.all([grens(true), grens(false)]);
  if (!eerste.rijen.length || !laatste.rijen.length) return null;
  return { eersteDag: String(eerste.rijen[0].dag).slice(0, 10), laatsteDag: String(laatste.rijen[0].dag).slice(0, 10) };
}

export interface MaandrapportData {
  locatieId: string;
  /** vandaag in de zone van de locatie */
  vandaag: string;
  eersteDag: string | null;
  laatsteDag: string | null;
  rijen: DagtotaalRij[];
}

/**
 * Alles wat het tabblad Maand nodig heeft, in één keer: het bereik en dan alle
 * dagrijen van de eerste dag tot en met vandaag. Dagrijen zijn klein, dus de
 * hele historie komt mee en de maandkiezer werkt zonder nieuwe ophaalslag.
 * Nul rijen is pas "nog geen dagtotaal" als de sessie ems mag lezen.
 */
export async function haalMaandrapport(client: EmsClient, locatieId: string, zone: string, opties: { nu?: Date } = {}): Promise<MaandrapportData> {
  eisZone(zone);
  const vandaag = vandaagSleutel(zone, opties.nu ?? new Date());
  const bereik = await haalDagtotaalBereik(client, locatieId);
  if (!bereik) {
    if (!(await magEmsLezen(client))) {
      throw new EmsFout('geen-toegang',
        'Deze sessie mag het schema ems niet lezen. Alleen admin, super_admin en crm_user hebben een policy op ems.');
    }
    return { locatieId, vandaag, eersteDag: null, laatsteDag: null, rijen: [] };
  }
  const tot = bereik.laatsteDag > vandaag ? bereik.laatsteDag : vandaag;
  const rijen = await haalDagtotalen(client, locatieId, bereik.eersteDag, tot);
  return { locatieId, vandaag, eersteDag: bereik.eersteDag, laatsteDag: bereik.laatsteDag, rijen };
}

/* ── de brug naar een Site (F9.12) ──────────────────────────────── */
/*
 * Het EMS hangt aan een Site (CONTEXT.md) via ems.locatie.site_id. Het CRM opent
 * het vanuit Locaties (/crm/sites/:siteId); /crm/ems en /crm/ems/:locatieId (met
 * een site) verwijzen daarheen door. Nul rijen is ook hier nooit stil "geen":
 * eerst vraagt has_role() of de sessie ems überhaupt mag lezen.
 */

export interface EmsLocatieVanSite { id: string; naam: string; plaats: string | null }

/**
 * De actieve EMS-locaties van één site, op naam. Leeg = er is geen
 * energiemonitoring ingericht (en de sessie mag ems wél lezen).
 */
export async function haalEmsLocatiesVoorSite(client: EmsClient, siteId: string): Promise<EmsLocatieVanSite[]> {
  if (typeof siteId !== 'string' || !UUID.test(siteId)) {
    throw new EmsFout('niet-gevonden', `"${String(siteId)}" is geen geldige site-id`);
  }
  const { rijen } = await haalAllePaginas<any>('ems.locatie', (van, tot, telling) =>
    ems(client, 'locatie').select('id, naam, plaats', telling ? { count: 'exact' } : undefined)
      .eq('site_id', siteId).eq('actief', true)
      .order('naam', { ascending: true }).order('id', { ascending: true })
      .range(van, tot), { sleutel: (r) => String(r.id) });
  if (!rijen.length) {
    const fout = await duidLeeg(client, 'geen locatie');
    if (fout.soort === 'geen-toegang') throw fout;
    return [];
  }
  return rijen.map((r) => ({ id: String(r.id), naam: r.naam, plaats: r.plaats ?? null }));
}

export interface EmsVanSite {
  /** De actieve EMS-locaties (haalEmsLocatiesVoorSite, met zijn actief-filter). */
  locaties: EmsLocatieVanSite[];
  /** Hoeveel inactieve locaties er aan de site hangen. Alleen geteld als er geen actieve is, anders 0. */
  uitgezet: number;
}

/**
 * Wat er onder een site hoort: de actieve locaties, en als die er niet zijn, of er
 * wel een uitgezette hangt. Zo is "nog geen energiemonitoring ingericht" te
 * onderscheiden van "de energiemonitoring staat uit", zonder dat de gewone lees
 * zijn filter op actief kwijtraakt: de uitgezette worden apart geteld, en niet
 * teruggegeven.
 */
export async function haalEmsVanSite(client: EmsClient, siteId: string): Promise<EmsVanSite> {
  const locaties = await haalEmsLocatiesVoorSite(client, siteId);
  if (locaties.length) return { locaties, uitgezet: 0 };
  /* Het leesrecht is hierboven al door has_role() bevestigd (lege lijst), dus nul
     is hier echt nul. */
  const { totaal } = await haalAllePaginas<any>('ems.locatie', (van, tot, telling) =>
    ems(client, 'locatie').select('id', telling ? { count: 'exact' } : undefined)
      .eq('site_id', siteId).eq('actief', false)
      .order('id', { ascending: true })
      .range(van, tot), { limiet: 1 });
  return { locaties, uitgezet: totaal };
}

/**
 * Welke sites een actieve EMS-locatie hebben, voor de hele lijst in één vraag
 * (niet per kaart). Een lege set betekent: geen enkele, en de sessie mag ems lezen.
 */
export async function haalSiteIdsMetEms(client: EmsClient): Promise<Set<string>> {
  const { rijen } = await haalAllePaginas<any>('ems.locatie', (van, tot, telling) =>
    ems(client, 'locatie').select('id, site_id', telling ? { count: 'exact' } : undefined)
      .eq('actief', true).not('site_id', 'is', null)
      .order('id', { ascending: true })
      .range(van, tot), { sleutel: (r) => String(r.id) });
  if (!rijen.length) {
    const fout = await duidLeeg(client, 'geen locatie met site');
    if (fout.soort === 'geen-toegang') throw fout;
  }
  return new Set(rijen.map((r) => String(r.site_id)));
}

/**
 * De site van één EMS-locatie, voor de doorverwijzing van een oude link
 * (/crm/ems/:locatieId). Ook een inactieve locatie: een oude link hoort bij zijn
 * site uit te komen, en de sitepagina zegt zelf dat er geen actieve monitoring is.
 */
export async function haalSiteVanLocatie(client: EmsClient, locatieId: string): Promise<{ gevonden: boolean; siteId: string | null }> {
  if (typeof locatieId !== 'string' || !UUID.test(locatieId)) return { gevonden: false, siteId: null };
  const { rijen } = await haalAllePaginas<any>('ems.locatie', (van, tot, telling) =>
    ems(client, 'locatie').select('id, site_id', telling ? { count: 'exact' } : undefined)
      .eq('id', locatieId)
      .range(van, tot), { limiet: 1 });
  if (!rijen.length) {
    const fout = await duidLeeg(client, 'geen locatie');
    if (fout.soort === 'geen-toegang') throw fout;
    return { gevonden: false, siteId: null };
  }
  return { gevonden: true, siteId: rijen[0].site_id ? String(rijen[0].site_id) : null };
}

export type EmsSectieStaat =
  | { soort: 'laden' }
  | { soort: 'geen-toegang'; fout: EmsFout }
  | { soort: 'fout'; fout: unknown }
  | { soort: 'geen-ems' }
  | { soort: 'ems-uit'; uitgezet: number }
  | { soort: 'ems'; locaties: EmsLocatieVanSite[] };

/**
 * Wat er onder een site staat. Vijf dingen die er op het scherm anders uitzien:
 * geen monitoring ingericht, monitoring die uit staat, geen recht om ems te
 * lezen, een storing, of het dashboard. Een lege lijst is alleen "geen-ems" als
 * has_role() het recht heeft bevestigd (zie haalEmsLocatiesVoorSite) en er ook
 * geen uitgezette locatie aan de site hangt (haalEmsVanSite).
 */
export function emsSectieVoor(q: { isPending: boolean; isError: boolean; error?: unknown; data?: EmsVanSite }): EmsSectieStaat {
  if (q.isError) {
    if (q.error instanceof EmsFout && q.error.soort === 'geen-toegang') return { soort: 'geen-toegang', fout: q.error };
    return { soort: 'fout', fout: q.error };
  }
  if (q.isPending || !q.data) return { soort: 'laden' };
  if (q.data.locaties.length) return { soort: 'ems', locaties: q.data.locaties };
  return q.data.uitgezet > 0 ? { soort: 'ems-uit', uitgezet: q.data.uitgezet } : { soort: 'geen-ems' };
}

export type Doorverwijzing =
  | { soort: 'laden' }
  | { soort: 'naar'; naar: string }
  | { soort: 'zonder-site' }
  | { soort: 'bestaat-niet' }
  | { soort: 'geen-toegang'; fout: EmsFout }
  | { soort: 'fout'; fout: unknown };

/**
 * Wat een oude link /crm/ems/:locatieId doet. Alleen een locatie met een site is
 * een doorverwijzing: naar die sitepagina. Een locatie zonder site, of een
 * onbekende of ongeldige id, krijgt een uitlegscherm — een stille sprong naar
 * Locaties laat de gebruiker raden wat er met zijn link is gebeurd. Een weigering
 * of een storing is evenmin een doorverwijzing: dan zou een fout er uitzien als
 * "geen site".
 */
export function doorverwijzingVoor(q: { isPending: boolean; isError: boolean; error?: unknown; data?: { gevonden: boolean; siteId: string | null } }): Doorverwijzing {
  if (q.isError) {
    if (q.error instanceof EmsFout && q.error.soort === 'geen-toegang') return { soort: 'geen-toegang', fout: q.error };
    return { soort: 'fout', fout: q.error };
  }
  if (q.isPending || !q.data) return { soort: 'laden' };
  if (q.data.siteId) return { soort: 'naar', naar: `/crm/sites/${q.data.siteId}` };
  return q.data.gevonden ? { soort: 'zonder-site' } : { soort: 'bestaat-niet' };
}
